'use client'
import { Command, CommandItem, CommandList } from '@/app/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/app/components/ui/popover';
import { scrollTop } from '@/lib/utils';
import { Role } from '@prisma/client';
import { ChevronDownIcon } from '@radix-ui/react-icons';
import { Flex, Text } from '@radix-ui/themes';
import { useSession } from 'next-auth/react';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import React, { useCallback, useEffect, useState } from 'react';

interface Props {
}

interface SortOption {
  id: string;
  label: string;
  role?: (Role | undefined)[];
}

const sortOptions: SortOption[] = [
  {
    id: 'lot',
    label: 'Lot #',
  },
  {
    id: 'activeBid,asc',
    label: 'Active bid (low to high)',
    role: [Role.ADMIN]
  },
  {
    id: 'activeBid,desc',
    label: 'Active bid (high to low)',
    role: [Role.ADMIN]
  },
  {
    id: 'bid,asc',
    label: 'Bid (low to high)',
    role: [Role.BUYER, undefined]
  },
  {
    id: 'bid,desc',
    label: 'Bid (high to low)',
    role: [Role.BUYER, undefined]
  },
  {
    id: 'bidCount,asc',
    label: 'Bid count (low to high)'
  },
  {
    id: 'bidCount,desc',
    label: 'Bid count (high to low)'
  },
  {
    id: 'watchCount,asc',
    label: 'Watch count (low to high)'
  },
  {
    id: 'watchCount,desc',
    label: 'Watch count (high to low)'
  }
];

const AuctionSort = () => {
  const session: any = useSession();

  const searchParams = useSearchParams();
  const { auctionId } = useParams();

  const sortParam = searchParams.get('orderBy');

  const getSortLabel = useCallback(() =>
    sortOptions.filter(option => option.id === sortParam)[0]?.label ?? "Lot #",
    [sortParam]
  );

  const [open, setOpen] = useState(false);
  const [sortText, setSortText] = useState(getSortLabel());
  const [hovered, setHovered] = useState(false);

  // Update sort label text when sort parameter changes
  useEffect(() => {
    setSortText(getSortLabel());
  }, [sortParam, setSortText, getSortLabel])

  // Injects sort parameters into the URL.
  const updateURLSearchParams = (param: string) => {
    const params = new URLSearchParams(window.location.search);
    // Collecting categories that are checked
    params.set("orderBy", param);

    const paramString = params.toString();
    const query = paramString ? "?" + paramString : "";
    window.history.pushState({}, '', `/auctions/${auctionId}` + query);
  };

  const onSelect = (option: SortOption) =>
    () => {
      setSortText(option.label);
      updateURLSearchParams(option.id);
      setOpen(false);
      scrollTop();
    }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger>
        <Flex
          className="text-sm font-medium"
          gap="2"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <Text className="whitespace-nowrap">Sort by:</Text>
          <Text
            className="font-normal underline underline-offset-2 whitespace-nowrap"
            style={{
              color: hovered ? '#444' : '#000'
            }}
          >{sortText}</Text>
          <Flex align="center">
            <ChevronDownIcon />
          </Flex>
        </Flex>
      </PopoverTrigger>
      <PopoverContent className="px-3">
        <Command>
          <CommandList>
            {sortOptions.filter(option => !option.role || option.role.includes(session.data?.role)).map((option, index) => (
              <CommandItem key={index} onSelect={onSelect(option)}
              >
                {option.label}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover >
  )
}

export default AuctionSort
